import { createStore } from 'zustand/vanilla';
import { isCancel } from 'axios';
import { RequestStatus } from '@/shared/model/requestStatus';
import { getErrorMessage } from '@/shared/lib/errors';
import { operatorTaskService } from '../api/operatorTaskService';
import { useOperatorTasks } from './store';
import { getTaskOriginKey, originToTaskQuery } from '../mappers';
import type { OperatorTaskOrigin, RelatedOperatorTaskStore } from './types';

/** Хранит задачи открытого заказа или диалога */
export const createRelatedOperatorTaskStore = (origin: OperatorTaskOrigin | null) => createStore<RelatedOperatorTaskStore>((
    set,
    get
) => ({
    origin,
    data: null,
    status: RequestStatus.Idle,
    error: null,
    revision: 0,
    first: 0,
    pageSize: 5,

    setOrigin: (next) => {
        if (getTaskOriginKey(next) === getTaskOriginKey(get().origin)) {
            set({ origin: next });

            return;
        }

        set({
            origin: next,
            data: null,
            status: RequestStatus.Idle,
            error: null,
            first: 0,
            revision: get().revision + 1
        });
    },

    setFirst: (first) => set({ first }),

    setPageSize: (size) => set({
        pageSize: Math.max(1, Math.min(size, useOperatorTasks.getState().options?.listMaxTake ?? 200)),
        first: 0,
        revision: get().revision + 1,
    }),

    fetch: async (signal) => {
        const state = get();

        if (!state.origin || !getTaskOriginKey(state.origin)) return;

        const revision = state.revision + 1;

        set({
            revision,
            status: RequestStatus.Loading,
            error: null
        });

        try {
            const data = await operatorTaskService.list(
                originToTaskQuery(state.origin, state.first, Math.min(state.pageSize, useOperatorTasks.getState().options?.listMaxTake ?? 200)), true, signal,
            );

            if (revision !== get().revision) return;

            if (state.first > 0 && state.first >= (data.allCount ?? 0)) {
                set({ first: 0 });

                return;
            }

            set({
                data,
                status: RequestStatus.Success
            });
        } catch (error: unknown) {
            if (revision !== get().revision || isCancel(error)) return;

            set({
                status: RequestStatus.Error,
                error: getErrorMessage(error, 'Не удалось загрузить связанные задачи')
            });
        }
    },
}));
